import { karachiBusinessDate } from '@/lib/dates'
import { sumPaisa } from '@/lib/money'
import { saleOutstanding } from '@/features/payments/outstanding'
import type { Category, Device, Expense, Payment, Purchase, Sale } from '@/offline/types'
import type { TxnRow } from '@/features/transactions/transactions'

export interface ReportInputs {
  purchases: Purchase[]
  sales: Sale[]
  payments: Payment[]
  expenses: Expense[]
  devices: Device[]
  categories: Category[]
}

export interface ReportFilters {
  fromKey?: string
  toKey?: string
  categoryId?: string | null
  brandCode?: string | null
}

export interface CategoryStat {
  categoryId: string
  name: string
  inStockCount: number
  stockCost: number
  realizedProfit: number
}

export interface ReportSummary {
  salesValue: number
  purchaseValue: number
  grossProfit: number
  expensesTotal: number
  netProfit: number
  receivables: number
  stockValue: number
  salesCount: number
  perCategory: CategoryStat[]
}

const keyOf = (iso: string) => karachiBusinessDate(iso)

/**
 * Profit & stock report. Gross profit = net sale proceeds - purchase cost - direct costs
 * (plan section 12); net profit deducts general expenses once. Receivables and stock value are
 * point-in-time balances (not range-limited); sales/purchases/expenses respect the date range.
 */
export function computeReport(inp: ReportInputs, f: ReportFilters = {}): ReportSummary {
  const deviceById = new Map(inp.devices.map((d) => [d.id, d]))
  const purchaseByDevice = new Map<string, Purchase>()
  for (const p of inp.purchases.filter((x) => !x.deleted_at)) purchaseByDevice.set(p.device_id, p)

  const costOfDevice = (deviceId: string) => {
    const p = purchaseByDevice.get(deviceId)
    return p ? p.cost_paisa + p.direct_cost_paisa : 0
  }
  const inRange = (k: string) => (!f.fromKey || k >= f.fromKey) && (!f.toKey || k <= f.toKey)
  const matches = (d: Device | undefined) =>
    !!d &&
    (f.categoryId == null || d.category_id === f.categoryId) &&
    (f.brandCode == null || d.brand_code === f.brandCode)

  const salesInRange = inp.sales.filter(
    (s) => !s.deleted_at && !s.returned_at && inRange(keyOf(s.sold_at)) && matches(deviceById.get(s.device_id)),
  )
  const purchasesInRange = inp.purchases.filter(
    (p) => !p.deleted_at && inRange(keyOf(p.purchased_at)) && matches(deviceById.get(p.device_id)),
  )
  const expensesInRange = inp.expenses.filter(
    (e) =>
      !e.deleted_at &&
      inRange(keyOf(e.spent_at)) &&
      (f.categoryId == null || e.category_id === f.categoryId),
  )

  const salesValue = sumPaisa(salesInRange.map((s) => s.sale_amount_paisa))
  const grossProfit = sumPaisa(salesInRange.map((s) => s.sale_amount_paisa - costOfDevice(s.device_id)))
  const purchaseValue = sumPaisa(purchasesInRange.map((p) => p.cost_paisa + p.direct_cost_paisa))
  const expensesTotal = sumPaisa(expensesInRange.map((e) => e.amount_paisa))
  const netProfit = grossProfit - expensesTotal

  const liveSales = inp.sales.filter((s) => !s.deleted_at && !s.returned_at)
  const receivables = sumPaisa(liveSales.map((s) => saleOutstanding(s, inp.payments)))

  const inStock = inp.devices.filter((d) => !d.deleted_at && d.status === 'in_stock' && matches(d))
  const stockValue = sumPaisa(inStock.map((d) => costOfDevice(d.id)))

  const perCategory: CategoryStat[] = inp.categories
    .filter((c) => !c.deleted_at && !c.archived_at)
    .map((c) => {
      const catStock = inStock.filter((d) => d.category_id === c.id)
      const catSales = salesInRange.filter((s) => deviceById.get(s.device_id)?.category_id === c.id)
      return {
        categoryId: c.id,
        name: c.name,
        inStockCount: catStock.length,
        stockCost: sumPaisa(catStock.map((d) => costOfDevice(d.id))),
        realizedProfit: sumPaisa(catSales.map((s) => s.sale_amount_paisa - costOfDevice(s.device_id))),
      }
    })
    .filter((s) => s.inStockCount > 0 || s.realizedProfit !== 0)

  return {
    salesValue,
    purchaseValue,
    grossProfit,
    expensesTotal,
    netProfit,
    receivables,
    stockValue,
    salesCount: salesInRange.length,
    perCategory,
  }
}

function csvEscape(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
}

/** Day-book rows to CSV (amounts in PKR decimal). */
export function transactionsToCsv(rows: TxnRow[]): string {
  const header = ['date', 'type', 'item', 'detail', 'amount_pkr', 'direction']
  const lines = rows.map((r) =>
    [r.dateKey, r.type, csvEscape(r.label), csvEscape(r.detail), (r.amountPaisa / 100).toFixed(2), r.direction].join(','),
  )
  return [header.join(','), ...lines].join('\n')
}
